import { useEffect, useId, type ReactNode } from "react"

type ModalProps = {
  children: ReactNode
  footer: ReactNode
  onClose: () => void
  open: boolean
  title: string
}

export function Modal({
  children,
  footer,
  onClose,
  open,
  title,
}: ModalProps) {
  const titleId = useId()

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }

    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", closeOnEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", closeOnEscape)
    }
  }, [onClose, open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 py-8 font-guest [font-synthesis:none]">
      <button
        aria-label="Close modal"
        className="absolute inset-0 bg-black/35"
        onClick={onClose}
        type="button"
      />

      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className="relative z-10 flex max-h-[calc(100vh-64px)] w-full max-w-[560px] flex-col overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white"
        role="dialog"
      >
        <header className="shrink-0 rounded-t-[8px] border border-[#dedede] bg-white px-4 py-4">
          <h2 className="text-base font-semibold leading-[19px] text-[#1c1c1f]" id={titleId}>
            {title}
          </h2>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-5 pt-4">{children}</div>

        <footer className="mt-auto flex shrink-0 justify-end gap-3 rounded-b-[8px] border border-[#e5e5e5] bg-white px-4 py-2">
          {footer}
        </footer>
      </section>
    </div>
  )
}
